import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import { Team } from '@/modules/team/models/Team';
import { Estate } from '@/modules/estate/models/Estate';
import { ModelType } from '@/modules/core/app/constants/ModelType';
import dayjs from 'dayjs';
import { DateFormat } from '@/modules/core/app/constants/date.constants';
import Lang from '@/modules/core/app/services/Lang';
import { Dayjs } from 'dayjs';
import { Opportunity } from '@/modules/opportunity/models/Opportunity';
import { OpportunityScanResult } from '@/modules/opportunity/constants/OpportunityScanResult';

export class OpportunityScan extends BaseEntityModel {
  id: number;
  teamId?: number;
  estateId?: number;
  isManual: boolean;
  result: string;

  team?: Relationship<Team | null>;
  estate?: Relationship<Estate | null>;
  opportunities?: Relationship<Opportunity[] | null>;

  constructor(model: Partial<OpportunityScan> = {}) {
    super(model);

    this.id = model.id!;
    this.teamId = model.teamId;
    this.estateId = model.estateId;
    this.isManual = model.isManual!;
    this.result = model.result!;

    this.team = Team.mapModelOrNull(model.team);
    this.estate = Estate.mapModelOrNull(model.estate);
    this.opportunities = Opportunity.mapModels(model.opportunities);
  }

  getModelName(): ModelType {
    return ModelType.OPPORTUNITY_SCAN;
  }

  get lastUpdatedDate(): string {
    const latestScanDate: Dayjs = dayjs(this.createdAt);

    let formattedDate: string = latestScanDate.format(
      DateFormat.MONTH_DAY_WITH_ORDINAL_TWELVE_HOUR
    );

    if (Math.abs(latestScanDate.diff(dayjs(), 'hours')) <= 5) {
      formattedDate = latestScanDate.fromNow();
    }

    return Lang.t('opportunity.scan-last-updated', {
      date: formattedDate
    });
  }

  get hasBeenExecutedLastDay(): boolean {
    // We use 1440 minutes instead of 24 hours to be more precise
    // as checking for 24 hours can lead to innacurate results since we
    // would only check hours.
    return Math.abs(dayjs(this.createdAt).diff(dayjs(), 'minutes')) < 1440;
  }

  get isSuccessful(): boolean {
    return [
      OpportunityScanResult.SUCCESS_NEW.key,
      OpportunityScanResult.SUCCESS_NO_NEW.key
    ].includes(this.result!);
  }

  get isPending(): boolean {
    return this.result === OpportunityScanResult.PENDING.key;
  }

  get notificationMessage(): string {
    if (!this.isSuccessful) {
      return Lang.t('opportunity.scan-unsuccessful');
    }

    const opportunitiesCount: number = this.opportunities?.length || 0;

    if (opportunitiesCount) {
      return Lang.tc('opportunity.scan-completed-with-new-opportunities', opportunitiesCount, {
        count: opportunitiesCount
      });
    }

    return Lang.t('opportunity.scan-completed-no-new-opportunities');
  }
}
